import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const gradeColors = {
  A: { bg: '#D1FAE5', border: '#22c55e', text: '#22c55e' },
  B: { bg: '#ECFCCB', border: '#84cc16', text: '#84cc16' },
  C: { bg: '#FFF9DB', border: '#f59e0b', text: '#f59e0b' },
  D: { bg: '#FFEDD5', border: '#f97316', text: '#f97316' },
  F: { bg: '#FEE2E2', border: '#ef4444', text: '#ef4444' },
};

const ScoreRing = ({ score, letter }) => {
  const color = gradeColors[letter] || gradeColors.F;
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: '160px', height: '160px' }}>
      <svg width="160" height="160" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="54" fill="none" stroke="#eee" strokeWidth="8" />
        <circle
          cx="60" cy="60" r="54" fill="none"
          stroke={color.text}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 60 60)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span data-testid="grader-letter-grade" style={{ fontSize: '3rem', fontWeight: 800, color: color.text, lineHeight: 1 }}>{letter}</span>
        <span data-testid="grader-score" style={{ fontSize: '0.875rem', color: '#555', marginTop: '0.25rem' }}>{score}/100</span>
      </div>
    </div>
  );
};

const CategoryCard = ({ category }) => {
  const pct = Math.round((category.score / category.max_score) * 100);
  const barColor = pct >= 80 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div data-testid={`grader-category-${category.name.toLowerCase().replace(/[^a-z]/g, '-')}`} style={{
      background: '#fff',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '1px solid var(--border-light-dark)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ fontWeight: 600, fontSize: '1rem', color: '#000' }}>{category.name}</h3>
        <span style={{ fontWeight: 700, color: barColor, fontSize: '1.125rem' }}>{category.score}/{category.max_score}</span>
      </div>

      {/* Progress bar */}
      <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', marginBottom: '1rem', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: barColor, borderRadius: '3px',
          transition: 'width 0.8s ease',
        }} />
      </div>

      {/* Details */}
      {category.details.map((d, i) => (
        <div key={`d-${i}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <CheckCircle size={16} color="#22c55e" style={{ flexShrink: 0, marginTop: '2px' }} />
          <span style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.7)', lineHeight: 1.4 }}>{d}</span>
        </div>
      ))}
      {category.failures.map((f, i) => (
        <div key={`f-${i}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <XCircle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <span style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.7)', lineHeight: 1.4 }}>{f}</span>
        </div>
      ))}
    </div>
  );
};

export const WebsiteGrader = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleGrade = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/api/grade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });

      if (!res.ok) throw new Error('Failed to grade website');
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError('Something went wrong. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ paddingTop: '5rem' }}>
      {/* Hero */}
      <section style={{
        background: '#FFD700',
        padding: '4rem 1.5rem 3rem',
        textAlign: 'center',
        minHeight: result ? 'auto' : '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.375rem 1rem',
            background: 'rgba(106, 13, 173, 0.1)',
            borderRadius: '9999px',
            marginBottom: '1.5rem',
          }}>
            <Globe size={16} color="#6A0DAD" />
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6A0DAD' }}>
              Free Website Grader
            </span>
          </div>

          <h1 className="heading-1" style={{ marginBottom: '1rem', fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
            How Does Your Restaurant Website Stack Up?
          </h1>
          <p className="body-large" style={{ color: '#555', marginBottom: '2rem' }}>
            Get an instant grade across 5 key areas: design, SEO, restaurant essentials, online ordering, and mobile experience.
          </p>

          {/* Input Form */}
          <form onSubmit={handleGrade} style={{
            display: 'flex',
            gap: '0.75rem',
            maxWidth: '560px',
            margin: '0 auto',
          }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Globe size={18} color="#777" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                data-testid="grader-url-input"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter restaurant website URL..."
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 44px',
                  fontSize: '1rem',
                  background: 'rgba(0, 0, 0, 0.05)',
                  border: '1px solid var(--border-light-dark)',
                  borderRadius: '12px',
                  color: '#000',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#6A0DAD'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-light-dark)'}
              />
            </div>
            <button
              data-testid="grader-submit-btn"
              type="submit"
              disabled={loading || !url.trim()}
              className="btn-primary"
              style={{
                padding: '14px 24px',
                whiteSpace: 'nowrap',
                opacity: loading || !url.trim() ? 0.6 : 1,
                cursor: loading || !url.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Scanning site...</>
              ) : (
                <>Grade It</>
              )}
            </button>
          </form>

          {error && (
            <div data-testid="grader-error" style={{
              marginTop: '1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              color: '#ef4444', fontSize: '0.875rem',
            }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      {result && (
        <section data-testid="grader-results" style={{ padding: '4rem 1.5rem', background: '#fff' }} className="section-dark">
          <div className="container" style={{ maxWidth: '900px' }}>
            {/* Score Summary */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              marginBottom: '3rem',
              padding: '2rem',
              background: '#fff',
              borderRadius: '16px',
              border: '1px solid var(--border-light-dark)',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              <ScoreRing score={result.overall_score} letter={result.letter_grade} />
              <div style={{ flex: 1, minWidth: '250px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#000', marginBottom: '0.5rem' }}>
                  Results for <span style={{ color: '#6A0DAD' }}>{result.url}</span>
                  {result.pages_scanned > 1 && (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#22c55e', marginLeft: '0.5rem' }}>
                      ({result.pages_scanned} pages scanned)
                    </span>
                  )}
                </h2>
                <p data-testid="grader-summary" style={{ color: 'rgba(0,0,0,0.7)', lineHeight: 1.6, fontSize: '0.9375rem' }}>
                  {result.summary}
                </p>
              </div>
            </div>

            {/* Category Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
              gap: '1.25rem',
              marginBottom: '3rem',
            }}>
              {result.categories.map((cat, i) => (
                <CategoryCard key={i} category={cat} />
              ))}
            </div>

            {/* CTA */}
            <div style={{
              textAlign: 'center',
              padding: '3rem 2rem',
              background: '#FFF9DB',
              borderRadius: '16px',
              border: '1px solid var(--border-light-dark)',
            }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#000', marginBottom: '0.75rem' }}>
                Want to Fix These Issues?
              </h2>
              <p style={{ color: 'rgba(0,0,0,0.7)', marginBottom: '1.5rem', maxWidth: '500px', margin: '0 auto 1.5rem' }}>
                HungrMates builds custom restaurant apps with online ordering, loyalty programs, and mobile-first design — everything your site is missing.
              </p>
              <Link to="/book-demo" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 32px' }}>
                Book a Free Demo <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
              </Link>
              <p style={{ fontSize: '0.875rem', color: '#777', marginTop: '1rem' }}>
                30-minute call. No commitment. See how we'd improve your score.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* How It Works (shown when no results) */}
      {!result && !loading && (
        <section style={{ padding: '4rem 1.5rem', background: '#fff' }} className="section-dark">
          <div className="container" style={{ maxWidth: '800px' }}>
            <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>
              What We Check
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.5rem',
            }}>
              {[
                { label: 'Visual Design & Mobile', desc: 'Responsive layout, accessibility, modern CSS' },
                { label: 'SEO & Search Visibility', desc: 'Title tags, meta descriptions, structured data' },
                { label: 'Restaurant Essentials', desc: 'Phone, address, hours, menu, social media' },
                { label: 'Online Ordering', desc: 'Direct ordering, reservations, gift cards' },
                { label: 'Mobile Ordering', desc: 'Mobile app, loyalty program, push notifications' },
              ].map((item, i) => (
                <div key={i} style={{
                  padding: '1.5rem',
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light-dark)',
                }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '8px',
                    background: 'rgba(106, 13, 173, 0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#6A0DAD', fontWeight: 700, fontSize: '1rem',
                    marginBottom: '0.75rem',
                  }}>{i + 1}</div>
                  <h3 style={{ fontWeight: 600, color: '#000', marginBottom: '0.375rem', fontSize: '0.9375rem' }}>{item.label}</h3>
                  <p style={{ color: '#555', fontSize: '0.8125rem', lineHeight: 1.4 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          form[style] {
            flex-direction: column !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WebsiteGrader;
